import React, { useState } from 'react'
import styled from 'styled-components'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import CheckIcon from '@mui/icons-material/Check'
import CloseIcon from '@mui/icons-material/Close'
import Card from 'pages/HehmDevice/HehmDashboard/Card'
import LastUpdatedText from 'components/LastUpdatedText'
import TabContentTitle from 'pages/HehmDevice/TabContentTitle'
import Tooltip from 'components/Tooltip'

import { color } from 'styles/colors'

import Api from 'services/api'
import GA4 from 'helpers/ga4'
import { shortNumericDateWithDayOfWeek } from 'helpers/formatters'
import { DeviceBase } from 'types/Device'
import { scrubText } from 'helpers/inputCleaners'

type Props = {
  device?: DeviceBase
}

const HehmNotesPanel = ({ device }: Props) => {
  const [note, setNote] = useState(device?.note ?? '')
  const [isEditing, setIsEditing] = useState(false)
  const [editValue, setEditValue] = useState(note)
  const [noteUpdatedAt, setNoteUpdatedAt] = useState<number | string>(device?.noteUpdatedAt ?? Date.now())

  const handleEditClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    setEditValue(note)
    setIsEditing(true)
    GA4.sendEvent({ category: 'PMK Settings', action: 'Click Hehm Notes Edit Button', label: 'Hehm Notes' })
  }

  const handleSave = async (e: React.MouseEvent, content: string | null = editValue) => {
    e.stopPropagation()
    const isDelete = content === null
    const noteUpdated = isDelete || content !== note
    GA4.sendEvent({ category: 'PMK Settings', action: 'Click Save Hehm Note', label: 'Hehm Notes' })

    if ((isDelete || noteUpdated) && device?.dealerId && device?.deviceId) {
      await Api.Device.update(device.dealerId, device.deviceId, {
        note: isDelete ? '' : content,
        noteUpdated,
        deviceType: device?.deviceType ? device.deviceType.toUpperCase() : undefined
      }).then(() => {
        if (device) device.note = isDelete ? '' : (content ?? '')
      })
      setNoteUpdatedAt(Date.now())
    }
    setNote(content ?? '')
    setIsEditing(false)
  }

  const handleCancel = (e: React.MouseEvent) => {
    e.stopPropagation()
    setEditValue(note)
    setIsEditing(false)
  }

  return (
    <>
      <TabContentTitle>Notes</TabContentTitle>
      <LastUpdatedText timestamp={noteUpdatedAt} />

      <S.Cards>
        <Card
          title={shortNumericDateWithDayOfWeek(new Date())}
          titleStyle={{ color: color('secondaryText'), fontSize: '15px', fontWeight: 'normal' }}
          headerActions={
            isEditing ? (
              <>
                <Tooltip title="Save">
                  <S.SaveIcon aria-label="Save" onClick={handleSave} />
                </Tooltip>
                <Tooltip title="Cancel">
                  <S.CancelIcon aria-label="Cancel" onClick={handleCancel} />
                </Tooltip>
              </>
            ) : (
              <>
                <Tooltip title="Edit">
                  <S.EditIcon aria-label="Edit" onClick={handleEditClick} />
                </Tooltip>
                <Tooltip title="Delete">
                  <S.DeleteIcon aria-label="Delete" onClick={(e) => handleSave(e, null)} />
                </Tooltip>
              </>
            )
          }
        >
          <S.NotesArea>
            {isEditing ? (
              <S.TextArea
                value={editValue}
                onChange={(e) => setEditValue(scrubText(e.target.value, true))}
                autoFocus
                onClick={(e) => e.stopPropagation()}
              />
            ) : (
              note
                ? <S.NoteText>{note}</S.NoteText>
                : <S.Placeholder>Add Notes Here</S.Placeholder>
            )}
          </S.NotesArea>
        </Card>
      </S.Cards>
    </>
  )
}

export default HehmNotesPanel

const S = {
  Cards: styled.div`
    max-width: 800px;
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: 10px;
    @media (max-width: 600px) {
      grid-template-columns: 1fr;
      max-width: 100%;
      gap: 6px;
      padding: 0 4px;
    }
  `,
  NotesArea: styled.div`
    min-height: 120px;
  `,
  NoteText: styled.p`
    margin: 0;
    line-height: 1.6;
    color: ${color('primaryText')};
  `,
  Placeholder: styled.span`
    color: ${color('ancillaryText')};
    font-style: italic;
    opacity: 0.6;
  `,
  TextArea: styled.textarea`
    width: 100%;
    min-height: 120px;
    border: 1px solid ${color('border')};
    border-radius: 6px;
    padding: 8px;
    font-size: 14px;
    font-family: inherit;
    color: ${color('primaryText')};
    resize: vertical;
    outline: none;
    box-sizing: border-box;
    @media (max-width: 600px) {
      font-size: 13px;
      padding: 6px;
      min-height: 80px;
    }
    &:focus {
      border-color: ${color('EditIcon')};
    }
  `,
  EditIcon: styled(EditIcon)`
    color: ${color('hehmNotesEditIcon')};
    cursor: pointer;
    width: 18px !important;
    height: 18px !important;
    font-size: 18px !important;
    &:hover {
      color: ${color('hehmNotesEditIconHover')};
    }
  `,
  DeleteIcon: styled(DeleteIcon)`
    color: ${color('hehmNotesDeleteIcon')};
    cursor: pointer;
    width: 18px !important;
    height: 18px !important;
    font-size: 18px !important;
    &:hover {
      color: ${color('hehmNotesDeleteIconHover')};
    }
  `,
  SaveIcon: styled(CheckIcon)`
    color: ${color('hehmNotesSaveIcon')};
    cursor: pointer;
    width: 18px !important;
    height: 18px !important;
    font-size: 18px !important;
    &:hover {
      color: ${color('hehmNotesSaveIconHover')};
    }
  `,
  CancelIcon: styled(CloseIcon)`
    color: ${color('hehmNotesCancelIcon')};
    cursor: pointer;
    width: 18px !important;
    height: 18px !important;
    font-size: 18px !important;
    &:hover {
      color: ${color('hehmNotesCancelIconHover')};
    }
  `
}
